import { ActionIcon, Badge, Tooltip, UnstyledButton } from '@mantine/core';
import {
  IconBed,
  IconChevronRight,
  IconPencil,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { trackBedPanelOpened, trackRoomActionClicked } from 'src/Services/Mixpanel/events';

import { canHaveBedsRoomType, ROOM_ICONS } from '../../referential';

import styles from './RoomCard.module.scss';
import { Props } from './RoomCard.types';

const RoomCard = ({
  room,
  servedRoomName,
  onOpen,
  onEdit,
  onToggleOutOfService,
  onDelete,
}: Props) => {
  const { t } = useTranslation();
  const Icon = ROOM_ICONS[room.type];
  const bedCount = room.beds.length;
  const canHaveBeds = canHaveBedsRoomType(room.type);
  const showBedPill = canHaveBeds || bedCount > 0;

  const stop = (event: React.MouseEvent) => event.stopPropagation();

  const handleBedPillClick = (event: React.MouseEvent) => {
    stop(event);
    trackBedPanelOpened({ source: 'pill' });
    onOpen(room.id, { openBedForm: bedCount === 0 });
  };

  const handleRowClick = () => {
    trackBedPanelOpened({ source: 'row' });
    onOpen(room.id);
  };

  return (
    <div
      className={`${styles.card} ${room.isOutOfService ? styles.outOfService : ''}`}
      onClick={handleRowClick}
    >
      <Icon size={20} className={styles.icon} />
      <div className={styles.body}>
        <div className={styles.titleRow}>
          <span className={styles.title}>{room.name}</span>
          {servedRoomName && (
            <Badge size="xs" variant="light" color="teal">
              {t('admin.structure.rooms.enSuite', {
                name: servedRoomName,
                defaultValue: 'Attenante à {{name}}',
              })}
            </Badge>
          )}
          {room.isOutOfService && (
            <Badge size="xs" variant="light" color="gray">
              {t('admin.structure.rooms.outOfService', 'Hors service')}
            </Badge>
          )}
        </div>
        <div className={styles.metaRow}>
          <span>{t(`admin.structure.roomTypes.${room.type}`, room.type)}</span>
          {room.areaSqm != null && (
            <>
              <span className={styles.metaDot}>·</span>
              <span>{room.areaSqm} m²</span>
            </>
          )}
          {room.features.length > 0 && (
            <>
              <span className={styles.metaDot}>·</span>
              <span>
                {room.features
                  .map(feature => t(`admin.structure.roomFeatures.${feature}`, feature))
                  .join(', ')}
              </span>
            </>
          )}
        </div>
        {showBedPill &&
          (bedCount > 0 ? (
            <UnstyledButton
              className={styles.bedPill}
              onClick={handleBedPillClick}
              aria-label={t('admin.structure.beds.pillAria', {
                count: bedCount,
                defaultValue: 'Voir le couchage ({{count}} au total)',
                defaultValue_other: 'Voir les couchages ({{count}} au total)',
              })}
            >
              <IconBed size={16} className={styles.bedPillIcon} />
              <span className={styles.bedPillCount}>
                {t('admin.structure.beds.pillCount', {
                  count: bedCount,
                  defaultValue: '{{count}} couchage',
                  defaultValue_other: '{{count}} couchages',
                })}
              </span>
              <span className={styles.bedPillDot}>·</span>
              <span className={styles.bedPillCapacity}>
                {t('admin.structure.beds.pillCapacity', {
                  count: room.capacity,
                  defaultValue: '{{count}} pers.',
                  defaultValue_other: '{{count}} pers.',
                })}
              </span>
              <IconChevronRight size={14} className={styles.bedPillChevron} />
            </UnstyledButton>
          ) : (
            <UnstyledButton
              className={`${styles.bedPill} ${styles.bedPillEmpty}`}
              onClick={handleBedPillClick}
              aria-label={t('admin.structure.beds.add', 'Ajouter un couchage')}
            >
              <IconPlus size={14} className={styles.bedPillIcon} />
              <span className={styles.bedPillLabel}>
                {t('admin.structure.beds.add', 'Ajouter un couchage')}
              </span>
            </UnstyledButton>
          ))}
      </div>

      {/* Les 3 actions restent visibles sans survol : un menu « ⋮ » ne se trouve pas. */}
      <div className={styles.actions}>
        <Tooltip label={t('common.edit', 'Modifier')} withArrow>
          <ActionIcon
            variant="light"
            size="lg"
            aria-label={t('common.edit', 'Modifier')}
            onClick={event => {
              stop(event);
              trackRoomActionClicked({ action: 'edit' });
              onEdit(room);
            }}
          >
            <IconPencil size={18} />
          </ActionIcon>
        </Tooltip>

        <Tooltip
          label={
            room.isOutOfService
              ? t('admin.structure.rooms.markInService', 'Remettre en service')
              : t('admin.structure.rooms.markOutOfService', 'Marquer hors service')
          }
          withArrow
        >
          <ActionIcon
            variant="light"
            size="lg"
            aria-label={
              room.isOutOfService
                ? t('admin.structure.rooms.markInService', 'Remettre en service')
                : t('admin.structure.rooms.markOutOfService', 'Marquer hors service')
            }
            onClick={event => {
              stop(event);
              trackRoomActionClicked({ action: 'toggle_out_of_service' });
              onToggleOutOfService(room);
            }}
          >
            {room.isOutOfService ? <IconPlayerPlay size={18} /> : <IconPlayerPause size={18} />}
          </ActionIcon>
        </Tooltip>

        <Tooltip label={t('common.delete', 'Supprimer')} withArrow>
          <ActionIcon
            variant="light"
            color="red"
            size="lg"
            aria-label={t('common.delete', 'Supprimer')}
            onClick={event => {
              stop(event);
              trackRoomActionClicked({ action: 'delete' });
              onDelete(room);
            }}
          >
            <IconTrash size={18} />
          </ActionIcon>
        </Tooltip>
      </div>
    </div>
  );
};

export default RoomCard;
